import { ExternalLink, Mail, Phone } from "lucide-react";
import type { ContactCategory } from "@shared-core/data/api-types";
import { Card } from "../../primitives/card";

export const CATEGORY_BADGE_CLASS: Record<ContactCategory, string> = {
  strategic: "bg-primary/15 text-primary",
  key: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  operational: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  unknown: "bg-muted text-muted-foreground",
};

export interface PersonCardData {
  id: string;
  name: string | null;
  jobTitle?: string | null;
  category?: ContactCategory | null;
  customerName?: string | null;
  email?: string | null;
  phone?: string | null;
  phone2?: string | null;
  linkedinUrl?: string | null;
  rationale?: string | null;
}

export function PersonCard({ person }: { person: PersonCardData }) {
  const displayName = person.name?.trim() || null;
  const category = person.category ?? null;
  const hasContact = Boolean(
    person.email || person.phone || person.phone2 || person.linkedinUrl,
  );

  return (
    <Card className="p-4 flex flex-col gap-3 h-full">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {displayName ? (
            <div className="font-medium truncate">{displayName}</div>
          ) : (
            <div className="font-medium truncate text-muted-foreground italic">
              Unnamed
            </div>
          )}
          {person.jobTitle && (
            <div className="text-sm text-muted-foreground truncate">
              {person.jobTitle}
            </div>
          )}
          {person.customerName && (
            <div className="text-xs text-muted-foreground mt-0.5 truncate">
              {person.customerName}
            </div>
          )}
        </div>
        {category && (
          <span
            className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 capitalize ${CATEGORY_BADGE_CLASS[category]}`}
          >
            {category}
          </span>
        )}
      </div>

      {person.rationale && (
        <p className="text-sm text-foreground/80 leading-relaxed break-words">
          {person.rationale}
        </p>
      )}

      {hasContact && (
        // Cards may now be wrapped in a click-to-open-profile container
        // (ContactsPage) — stop propagation on these so tapping an actual
        // contact method doesn't also navigate to the profile.
        <div
          className="mt-auto pt-2 space-y-1 text-sm"
          onClick={(e) => e.stopPropagation()}
        >
          {person.email && (
            <a
              href={`mailto:${person.email}`}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-primary truncate"
            >
              <Mail className="size-3.5 shrink-0" />
              <span className="truncate">{person.email}</span>
            </a>
          )}
          {person.phone && (
            <a
              href={`tel:${person.phone}`}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-primary text-xs"
            >
              <Phone className="size-3.5 shrink-0" />
              <span>{person.phone}</span>
            </a>
          )}
          {person.phone2 && (
            <a
              href={`tel:${person.phone2}`}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-primary text-xs"
            >
              <Phone className="size-3.5 shrink-0" />
              <span>{person.phone2}</span>
            </a>
          )}
          {person.linkedinUrl && (
            <a
              href={person.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <ExternalLink className="size-3" />
              LinkedIn
            </a>
          )}
        </div>
      )}
    </Card>
  );
}
